import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Edit, Globe, MapPin, Phone, Plus, Save, Trash2, X } from 'lucide-react';
import { Input } from '../components/shared/UI';

const CustomerModule = ({ customers, onAdd, onEdit, onDelete, loading, readOnly }) => {
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const initialFormState = { 
    name: '', phone: '', email: '', website: '',
    address: '', city: '', state: '', zip: '', country: 'Indonesia',
    isBillingSame: true,
    billingAddress: '', billingCity: '', billingState: '', billingZip: '', billingCountry: ''
  };
  const [formData, setFormData] = useState(initialFormState);
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});

  const filteredCustomers = customers.filter(c => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return c.name?.toLowerCase().includes(query) || 
           c.email?.toLowerCase().includes(query) ||
           c.phone?.toLowerCase().includes(query);
  });

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Nama pelanggan wajib diisi';
    if (!formData.email.trim()) newErrors.email = 'Email wajib diisi';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Format email tidak valid';
    if (!formData.phone.trim()) newErrors.phone = 'Nomor telepon wajib diisi';
    if (!formData.address.trim()) newErrors.address = 'Alamat wajib diisi';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    if (editingId) {
      await onEdit({ ...formData, id: editingId });
    } else {
      await onAdd(formData);
    }
    resetForm();
  };

  const resetForm = () => {
    setFormData(initialFormState);
    setShowForm(false);
    setEditingId(null);
    setErrors({});
  };

  const handleEditClick = (customer) => {
    setFormData(customer);
    setEditingId(customer.id);
    setShowForm(true);
  };

  return (
    <div className="space-y-6 animate-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-heading font-bold tracking-tight">Pelanggan</h2>
          <p className="text-muted-foreground mt-1">Kelola data pelanggan Anda</p>
        </div>
        {!readOnly && (
        <button 
          onClick={() => showForm ? resetForm() : setShowForm(true)} 
          className={showForm ? "btn-secondary" : "btn-primary"}
          data-testid="add-customer-btn"
        >
          {showForm ? <><X size={18}/> Batal</> : <><Plus size={18}/> Tambah Pelanggan</>}
        </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="card p-4">
        <div className="relative w-full lg:w-80">
          <input
            type="text"
            placeholder="Cari pelanggan..."
            className="input-field pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-input"
          />
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      <AnimatePresence>
        {showForm && !readOnly && (
          <motion.form 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            onSubmit={handleSubmit} 
            className="card p-6 space-y-6"
            data-testid="customer-form"
          >
            <h3 className="font-heading font-semibold text-lg border-b border-border pb-4">
              {editingId ? 'Edit Pelanggan' : 'Tambah Pelanggan Baru'}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              <div className="flex flex-col space-y-2">
                <label className="label">Nama Pelanggan <span className="text-destructive">*</span></label>
                <input 
                  className={`input-field ${errors.name ? 'border-destructive focus:ring-destructive/50' : ''}`}
                  value={formData.name} 
                  onChange={e => { setFormData({...formData, name: e.target.value}); setErrors({...errors, name: ''}); }}
                  placeholder="PT. Contoh Perusahaan" 
                />
                {errors.name && <span className="text-xs text-destructive">{errors.name}</span>}
              </div>
              <div className="flex flex-col space-y-2">
                <label className="label">Email <span className="text-destructive">*</span></label>
                <input 
                  type="email"
                  className={`input-field ${errors.email ? 'border-destructive focus:ring-destructive/50' : ''}`}
                  value={formData.email} 
                  onChange={e => { setFormData({...formData, email: e.target.value}); setErrors({...errors, email: ''}); }}
                  placeholder="email@perusahaan.com" 
                />
                {errors.email && <span className="text-xs text-destructive">{errors.email}</span>}
              </div>
              <div className="flex flex-col space-y-2">
                <label className="label">Telepon <span className="text-destructive">*</span></label>
                <input 
                  className={`input-field ${errors.phone ? 'border-destructive focus:ring-destructive/50' : ''}`}
                  value={formData.phone} 
                  onChange={e => { setFormData({...formData, phone: e.target.value}); setErrors({...errors, phone: ''}); }}
                  placeholder="021-xxx-xxxx" 
                />
                {errors.phone && <span className="text-xs text-destructive">{errors.phone}</span>}
              </div>
              <Input label="Website" value={formData.website} onChange={e => setFormData({...formData, website: e.target.value})} placeholder="www.perusahaan.com" />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Alamat Utama</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <div className="md:col-span-2 flex flex-col space-y-2">
                  <label className="label">Alamat <span className="text-destructive">*</span></label>
                  <textarea 
                    className={`input-field resize-none ${errors.address ? 'border-destructive focus:ring-destructive/50' : ''}`}
                    rows="2" 
                    value={formData.address} 
                    onChange={e => { setFormData({...formData, address: e.target.value}); setErrors({...errors, address: ''}); }}
                    placeholder="Jl. Contoh No. 123"
                  />
                  {errors.address && <span className="text-xs text-destructive">{errors.address}</span>}
                </div>
                <Input label="Kota" value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} />
                <Input label="Provinsi" value={formData.state} onChange={e => setFormData({...formData, state: e.target.value})} />
                <Input label="Kode Pos" value={formData.zip} onChange={e => setFormData({...formData, zip: e.target.value})} />
                <Input label="Negara" value={formData.country} onChange={e => setFormData({...formData, country: e.target.value})} />
              </div>
            </div>

            <div className="bg-slate-50 p-4 rounded-xl">
              <label className="flex items-center gap-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  className="w-5 h-5 rounded border-slate-300 text-primary focus:ring-primary"
                  checked={formData.isBillingSame}
                  onChange={e => setFormData({...formData, isBillingSame: e.target.checked})}
                />
                <span className="text-sm font-medium">Alamat penagihan sama dengan alamat utama</span>
              </label>
              
              <AnimatePresence>
                {!formData.isBillingSame && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4"
                  >
                    <div className="md:col-span-2">
                      <label className="label">Alamat Penagihan</label>
                      <textarea className="input-field resize-none" rows="2" value={formData.billingAddress} onChange={e => setFormData({...formData, billingAddress: e.target.value})} />
                    </div>
                    <Input label="Kota" value={formData.billingCity} onChange={e => setFormData({...formData, billingCity: e.target.value})} />
                    <Input label="Provinsi" value={formData.billingState} onChange={e => setFormData({...formData, billingState: e.target.value})} />
                    <Input label="Kode Pos" value={formData.billingZip} onChange={e => setFormData({...formData, billingZip: e.target.value})} />
                    <Input label="Negara" value={formData.billingCountry} onChange={e => setFormData({...formData, billingCountry: e.target.value})} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="flex justify-end pt-4">
              <button type="submit" className="btn-primary" data-testid="save-customer-btn">
                <Save size={16}/> {editingId ? 'Perbarui' : 'Simpan'}
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="table-header">
              <tr>
                <th className="px-6 py-4 text-left">No</th>
                <th className="px-6 py-4 text-left">Nama</th>
                <th className="px-6 py-4 text-left">Kontak</th>
                <th className="px-6 py-4 text-left">Lokasi</th>
                <th className="px-6 py-4 text-left">Website</th>
                {!readOnly && <th className="px-6 py-4 text-right">Aksi</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={readOnly ? "5" : "6"} className="px-6 py-12 text-center text-muted-foreground">Memuat data...</td></tr>
              ) : filteredCustomers.length === 0 ? (
                <tr><td colSpan={readOnly ? "5" : "6"} className="px-6 py-12 text-center text-muted-foreground">{searchQuery ? 'Tidak ada hasil ditemukan.' : 'Belum ada pelanggan.'}</td></tr>
              ) : filteredCustomers.map((customer, index) => (
                <tr key={`customer-${customer.id}-${index}`} className="table-row">
                  <td className="px-6 py-4 text-muted-foreground">{index + 1}</td>
                  <td className="px-6 py-4 font-medium text-primary">{customer.name}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1 text-muted-foreground text-xs"><Phone size={12}/> {customer.phone || '-'}</div>
                    <div className="text-xs text-muted-foreground mt-1">{customer.email || '-'}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1 text-muted-foreground text-xs"><MapPin size={12}/> {customer.city || '-'}</div>
                  </td>
                  <td className="px-6 py-4">
                    {customer.website ? (
                      <a href={`https://${customer.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-accent hover:underline text-xs">
                        <Globe size={12}/> Link
                      </a>
                    ) : '-'}
                  </td>
                  <td className="px-6 py-4">
                    {!readOnly && (
                    <div className="flex justify-end gap-1">
                      <button onClick={() => handleEditClick(customer)} className="p-2 hover:bg-slate-100 rounded-lg text-muted-foreground hover:text-primary transition-colors" data-testid={`edit-customer-${customer.id}`}><Edit size={16}/></button>
                      <button onClick={() => onDelete(customer.id)} className="p-2 hover:bg-rose-50 rounded-lg text-muted-foreground hover:text-destructive transition-colors" data-testid={`delete-customer-${customer.id}`}><Trash2 size={16}/></button>
                    </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


export default CustomerModule;
